import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import FavoriteButton from "@/components/FavoriteButton";
import { PROGRAMS, PROGRAM_COLORS, PROGRAM_LABELS, getProgramBySlug } from "@/data/programs";
import { ArrowLeft, Quote } from "lucide-react";

export default function ProgramDetail() {
  const { slug } = useParams();
  const program = getProgramBySlug(slug);
  const related = useMemo(() => {
    if (!program) return [];
    return PROGRAMS.filter((p) => p.key === program.key && p.slug !== program.slug).slice(0, 3);
  }, [program]);

  if (!program) return <Navigate to="/programs" replace />;

  const color = PROGRAM_COLORS[program.key];
  const label = PROGRAM_LABELS[program.key];

  return (
    <>
      <SEO
        title={`${program.title} · ${label}`}
        description={program.tagline}
        image={program.image}
        path={`/programs/${program.slug}`}
        type="article"
      />

      {/* Image hero */}
      <section className="relative pt-4" data-testid={`program-detail-${program.slug}`}>
        <div className="max-w-[1240px] mx-auto px-6">
          <Link to="/programs" className="inline-flex items-center gap-2 text-ink/70 no-underline text-sm mb-6 hover:text-palm" data-testid="program-back-btn">
            <ArrowLeft size={16} /> All programs
          </Link>
          <div className="relative rounded-[16px] overflow-hidden">
            <div className="aspect-[16/8] md:aspect-[16/6] w-full bg-ink/10">
              <img src={program.image} alt="" className="w-full h-full object-cover" />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(180deg, transparent 30%, ${color}CC 100%)` }}
              />
            </div>
            <FavoriteButton slug={program.slug} className="absolute top-4 right-4 z-10" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-white">
              <span className="inline-block bg-white/15 backdrop-blur border border-white/25 rounded-full px-3 py-1 text-xs uppercase tracking-[0.14em] font-bold">{label}</span>
              {program.isNew && (
                <span className="inline-block ml-2 bg-mango text-palm-deep rounded-full px-3 py-1 text-xs uppercase tracking-[0.14em] font-bold">New</span>
              )}
              <h1 className="mt-3 text-[clamp(34px,4.5vw,54px)] font-display font-bold leading-[1.05] drop-shadow-lg">
                {program.title}
              </h1>
              {program.full && <p className="mt-2 text-white/90 font-semibold text-lg drop-shadow">{program.full}</p>}
            </div>
          </div>
          <p className="mt-6 max-w-[62ch] text-ink/85 text-[19px]">{program.tagline}</p>
        </div>
      </section>

      {/* Body + sidebar */}
      <section className="py-16">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.4fr_.6fr] gap-12 items-start">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <div>
              {program.body.split("\n\n").map((para, i) => (
                <p key={i} className="mb-4 text-ink/85 text-[17px] leading-[1.75]">{para}</p>
              ))}
            </div>

            {program.highlights && program.highlights.length > 0 && (
              <>
                <h3 className="font-display text-2xl mt-10 mb-4">Program highlights</h3>
                <ul className="space-y-2.5">
                  {program.highlights.map((h) => (
                    <li key={h} className="flex gap-3">
                      <span className="mt-2 w-2 h-2 rounded-sm shrink-0" style={{ background: color }} />
                      <span className="text-ink/85">{h}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {program.testimonials && program.testimonials.length > 0 && (
              <div className="mt-14" data-testid="program-testimonials">
                <Eyebrow>Voices from the program</Eyebrow>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  {program.testimonials.map((t, i) => (
                    <figure
                      key={i}
                      className="relative bg-white border border-ink/10 rounded-[14px] p-6"
                    >
                      <Quote className="absolute -top-3 -left-3 bg-white rounded-full p-1 border border-ink/10" size={26} style={{ color }} />
                      <blockquote className="text-ink/85 text-[16px] leading-[1.7] italic">
                        "{t.quote}"
                      </blockquote>
                      <figcaption className="mt-4 text-sm">
                        <div className="font-semibold text-ink">{t.name}</div>
                        <div className="text-ink/55">{t.role}</div>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="bg-white border border-ink/10 rounded-[14px] p-6 lg:sticky lg:top-24">
            {program.impact && (
              <>
                <span className="text-xs uppercase tracking-[0.14em] font-bold text-palm">Impact so far</span>
                <p className="mt-2 text-ink/85 text-[15px]">{program.impact}</p>
                <div className="mt-6 stripe-rule" aria-hidden="true"><span/><span/><span/><span/><span/></div>
              </>
            )}
            <p className={`${program.impact ? "mt-6" : ""} text-ink/70 text-sm`}>Want to strengthen this thread?</p>
            <div className="mt-3 flex flex-col gap-2">
              <Link to="/get-involved#donate" className="bg-palm text-white text-center font-bold rounded-full px-5 py-2.5 no-underline hover:shadow-xl transition" data-testid="program-donate-cta">Donate to HOFA</Link>
              <Link to="/get-involved#volunteer" className="bg-white border-2 border-palm text-palm text-center font-bold rounded-full px-5 py-2.5 no-underline hover:bg-palm hover:text-white transition" data-testid="program-volunteer-cta">Volunteer</Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 bg-ink/[.03] border-t border-ink/10">
          <div className="max-w-[1140px] mx-auto px-6">
            <Eyebrow>More {label.toLowerCase()} work</Eyebrow>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/programs/${p.slug}`}
                  data-testid={`related-program-${p.slug}`}
                  className="bg-white border border-ink/10 rounded-[14px] overflow-hidden no-underline text-ink hover:-translate-y-1 hover:shadow-xl transition"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-ink/5">
                    <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex flex-col gap-2">
                    <span
                      className="self-start text-[11px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 rounded-full text-white"
                      style={{ background: color }}
                    >
                      {label}
                    </span>
                    <h3 className="font-display text-lg">{p.title}</h3>
                    <p className="text-[14.5px] text-ink/70">{p.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
